/* eslint-disable react/no-unknown-property */
import React from 'react';
import { observer } from 'mobx-react-lite';
import { Tenancy_Rich_Asset_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import { NodeColumns, TableRow, tableStore } from '../../../stores/table-store';
import { getNodeColumnDisplayName } from '../../../stores/utils/column-display-names';
import { getColumnPropertySpec } from '../../../stores/utils/column-property-spec';
import { getFieldHelp } from '../expanded-cell-view/field-hint';
import { autosizeTextarea } from '../expanded-cell-view/autosize-textarea';
import { ASSET_LABELS, getAssetTypeList } from '../utils/get-asset-type-list';
import { NodeBehaviorsMultiSelect } from '../node-behaviors-multi-select';
import {
  commitAssetTypeChange,
  commitCommandChange,
  getCellErrorMessage,
  writeBehaviors,
} from '../src/cell';
import { useNodeFieldCommit } from './use-node-field-commit';
import { useBobcatFlag } from '../../../hooks/use-bobcat-flag';
import { CommandScriptLink } from './command-script-link';
import { ParameterInputField } from './parameter-input-field';

/**
 * One labelled field in the node property panel.
 *
 * Every editor here commits through the SAME store function the grid's own
 * cell uses — `commitAssetTypeChange`, `commitCommandChange`, `writeBehaviors`,
 * `updateRowType`, `writeCellValue`, or the draft/commit pair in
 * `useNodeFieldCommit`. That is deliberate and is the main invariant of this
 * file: each of those carries side effects the panel must not reimplement
 * (asset type auto-adjusts behaviours and seeds default content; a node-type
 * change clears type-dependent fields as ONE undo entry; behaviours keep
 * `nluDisabled` in sync). An editor that wrote the column directly would look
 * right and quietly desynchronise the row.
 *
 * Controls are native elements styled to the design system rather than the
 * Lit components. The Expanded Cell View already does this, and it sidesteps
 * three known `@lit/react` hazards in one go: `ps-simple-select` builds its
 * options inside a `requestIdleCallback` and so needs its value re-asserted
 * after mount, `ps-select-option` rejects values containing spaces (asset
 * labels and command names both have them), and `ps-select` BLANKS a value
 * with no matching option. The tab bar is the design-system `TabGroup`, which
 * has none of those problems and is already used this way by the Supervisor
 * panel.
 */
export const NodePropertyField: React.FC<{
  rowId: string;
  columnId: NodeColumns;
  viewOnly?: boolean;
  commandOptions: string[];
  commandKnownFunctions: Set<string>;
}> = observer(
  ({ rowId, columnId, viewOnly, commandOptions, commandKnownFunctions }) => {
    const spec = getColumnPropertySpec(columnId);
    const label = getNodeColumnDisplayName(columnId);
    const help = getFieldHelp(spec);
    const error = getCellErrorMessage(rowId, columnId);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const value = tableStore.cellValueForEditor(rowId, columnId);
    const text = value == null ? '' : String(value);

    const { onFocus, onChange, onBlur } = useNodeFieldCommit(rowId, columnId);
    const parameterInputDrilldownEnabled =
      useBobcatFlag('fiveStageNav') === 'allowed';

    // The grid's own rule, verbatim (`cell.tsx`: `disabled = !editing ||
    // ruleDisabled`), plus the viewer role:
    //
    // - `!tableStore.isEditing()` is the table's View Mode lock. It is NOT the
    //   same thing as `viewOnly`/`isViewer`: an editor who has flipped the
    //   toolbar's Edit/View toggle has a read-only GRID, and without this the
    //   panel would happily write every field anyway. (The behaviours
    //   multi-select already goes read-only on its own `isEditing()` gate,
    //   which is what made the inconsistency visible.)
    // - Rule-disabled cells (e.g. Parameter Input while the node syncs with the
    //   Supervisor config) come from the same predicate the grid uses, so the
    //   two surfaces never disagree about what is editable.
    const ruleDisabled = tableStore.isCellDisabled(
      rowId,
      columnId as keyof TableRow
    );
    const disabled = viewOnly || !tableStore.isEditing() || ruleDisabled;

    React.useLayoutEffect(() => {
      autosizeTextarea(textareaRef.current);
    }, [text, rowId, columnId]);

    const control = (() => {
      switch (spec.kind) {
        case 'checkbox':
          return (
            <input
              type="checkbox"
              className="c-node-property__checkbox"
              test-id={`ps-node-property-checkbox-${columnId}`}
              // PSTUDIO-2425, mirrored from the grid cell: when NLU Disabled is
              // rule-disabled (Free Text off) NLU is not applicable, so the box
              // is drawn from the RULE and not from the stored value —
              // unchecked for ordinary rows, checked for file_upload rows,
              // which the PIF compiler requires nluDisabled=true for. Reading
              // the raw value here made the panel contradict the grid for any
              // row carrying a stale flag from the DB or a CSV import.
              // `Boolean(...)` and not `=== true`, also from the grid: an
              // imported row can hold a truthy non-boolean.
              checked={
                columnId === NodeColumns.nluDisabled && ruleDisabled
                  ? tableStore.getValue(rowId, NodeColumns.richAssetType) ===
                    Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload
                  : Boolean(
                      tableStore.getValue(rowId, columnId as keyof TableRow)
                    )
              }
              disabled={disabled}
              onChange={(e) =>
                tableStore.writeCellValue(rowId, columnId, e.target.checked)
              }
            />
          );

        case 'multiselect':
          // Behaviours — the SAME component the grid cell renders, committing
          // through the shared `writeBehaviors` so the `nluDisabled` side
          // effect stays in sync.
          //
          // `readOnly` carries the panel's full `disabled` rule across. The
          // component's own gate is `tableStore.isEditing()` alone, which is
          // the grid's rule but NOT the panel's — without this a viewer (or a
          // rule-disabled cell) got a live picker here while every other field
          // beside it was disabled.
          return (
            <NodeBehaviorsMultiSelect
              rowId={rowId}
              columnId={columnId}
              readOnly={disabled}
              onEdit={(values: string[] | undefined) =>
                writeBehaviors(rowId, values ?? [])
              }
            />
          );

        case 'number':
          return (
            <input
              type="number"
              className="c-node-property__input"
              test-id={`ps-node-property-number-${columnId}`}
              // Keyed on the value so an EXTERNAL change (undo, paste, a
              // renumber) re-mounts this uncontrolled input. Without the key it
              // would keep showing the stale number it was mounted with.
              key={`${rowId}:${columnId}:${text}`}
              defaultValue={text}
              disabled={disabled}
              onBlur={(e) => {
                const raw = e.currentTarget.value;
                const next = raw === '' ? undefined : Number(raw);
                // Only write when the value ACTUALLY changed — the same guard
                // `commitCellEditing` applies via `nativeCellRawEquals`. Writing
                // unconditionally meant merely tabbing through Node Number ran
                // `updateRow({ saveImmediately: true })` and queued a network
                // save for a row nothing had changed on. `recordRowEdit`
                // suppresses the empty UNDO entry, but only after the write and
                // the save have already happened.
                if (
                  next ===
                  tableStore.getValue(rowId, columnId as keyof TableRow)
                ) {
                  return;
                }
                tableStore.writeCellValue(rowId, columnId, next);
              }}
            />
          );

        case 'select':
          if (columnId === NodeColumns.nodeType) {
            return (
              <select
                className="c-node-property__select"
                test-id="ps-node-property-node-type"
                value={text}
                disabled={disabled}
                onChange={(e) => {
                  const next = e.target.value as TableRow['nodeType'];
                  // updateRowType CLEARS the fields that do not apply to the
                  // new type, so this must be one undo entry with them —
                  // exactly what the grid's node-type cell does.
                  tableStore.recordRowEdit(
                    rowId,
                    'Change node type',
                    () => {
                      tableStore.updateRowType({ rowId, newRowType: next });
                      tableStore.clearCellValidationError(
                        rowId,
                        NodeColumns.nodeType
                      );
                    },
                    NodeColumns.nodeType
                  );
                }}
              >
                <option value="d">Decision Node</option>
                <option value="a">Action Node</option>
              </select>
            );
          }

          if (columnId === NodeColumns.richAssetType) {
            const known = getAssetTypeList();
            const knownAsset = known.some(({ type }) => type === text);
            const currentAssetLabel = text
              ? (ASSET_LABELS[text as keyof typeof ASSET_LABELS] ?? text)
              : '';
            return (
              <select
                className="c-node-property__select"
                test-id="ps-node-property-asset-type"
                value={text}
                disabled={disabled}
                onChange={(e) => commitAssetTypeChange(rowId, e.target.value)}
              >
                {/* A stored type that is no longer offered (legacy, or removed
                  from the channel's list) is kept as its own option, so the
                  controlled select is not rendered empty and a stray change
                  event cannot silently overwrite it. Mirrors the grid cell —
                  including the UNSET case: with no `text` there is still no
                  matching option, and without a `value=""` entry the select
                  renders with nothing selected and no way to say "none". The
                  label goes through ASSET_LABELS so a legacy type reads the
                  same here as it does in the grid. */}
                {!knownAsset && (
                  <option value={text}>{currentAssetLabel || '—'}</option>
                )}
                {known.map(({ type, label: assetLabel }) => (
                  <option key={type} value={type}>
                    {assetLabel}
                  </option>
                ))}
              </select>
            );
          }

          if (columnId === NodeColumns.command) {
            return (
              <>
                <select
                  className="c-node-property__select"
                  test-id="ps-node-property-command"
                  value={text}
                  disabled={disabled}
                  onChange={(e) =>
                    commitCommandChange(
                      rowId,
                      e.target.value,
                      commandKnownFunctions
                    )
                  }
                >
                  {!text && <option value="">—</option>}
                  {commandOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {/* The script behind this command, in the code editor. */}
                <CommandScriptLink command={text} />
              </>
            );
          }

          // A `select` kind with a static option set from the spec.
          return (
            <select
              className="c-node-property__select"
              test-id={`ps-node-property-select-${columnId}`}
              value={text}
              disabled={disabled}
              onChange={(e) =>
                tableStore.writeCellValue(rowId, columnId, e.target.value)
              }
            >
              {(spec.options ?? []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );

        case 'multiline':
        case 'json':
          // Project Bobcat: Parameter Input's JSON blob gets a section
          // drill-down instead of a flat textarea — see
          // `parameter-input-field.tsx`. Every other 'json'/'multiline'
          // column (analyticsEvent, description, utterances, …) is
          // untouched, and Parameter Input itself renders exactly as before
          // when the flag is off.
          if (
            columnId === NodeColumns.parameterInput &&
            parameterInputDrilldownEnabled
          ) {
            return (
              <ParameterInputField
                rowId={rowId}
                raw={text}
                disabled={disabled}
                commit={{ onFocus, onChange, onBlur }}
              />
            );
          }
          return (
            <textarea
              ref={textareaRef}
              className="c-node-property__textarea"
              test-id={`ps-node-property-textarea-${columnId}`}
              value={text}
              disabled={disabled}
              onFocus={onFocus}
              onChange={(e) => onChange(e.currentTarget.value)}
              onBlur={onBlur}
            />
          );

        case 'readonly':
          return (
            <span className="c-node-property__readonly">{text || '—'}</span>
          );

        // `text`, `stringList` and `nodeRefs` all edit as one line. A list
        // column is a delimited string to the user — the design shows them as
        // single-line inputs — and the delimiter is taught by the help line
        // below rather than by giving them a taller box they do not need.
        case 'text':
        case 'stringList':
        case 'nodeRefs':
        default:
          return (
            <input
              type="text"
              className="c-node-property__input"
              test-id={`ps-node-property-input-${columnId}`}
              value={text}
              disabled={disabled}
              onFocus={onFocus}
              onChange={(e) => onChange(e.currentTarget.value)}
              onBlur={onBlur}
            />
          );
      }
    })();

    return (
      <div
        className="c-node-property"
        test-id={`ps-node-property-${columnId}`}
        data-column={columnId}
      >
        <label className="c-node-property__label">
          {label}
          {spec.required && (
            // The GRAMMAR marks every field optional; these are the ones the
            // compiler's VALIDATORS reject a node without. The validators are
            // what actually blocks a publish, so they are what the editor
            // reflects.
            <span
              className="c-node-property__required"
              title="Required — the compiler rejects a node without this"
              aria-label="required"
            >
              {' *'}
            </span>
          )}
        </label>
        <div
          className={`c-node-property__control${
            error ? ' c-node-property__control--error' : ''
          }`}
        >
          {control}
        </div>
        {error && (
          <div
            className="c-node-property__error"
            test-id={`ps-node-property-error-${columnId}`}
          >
            {error}
          </div>
        )}
        {help && !error && (
          <div
            className="c-node-property__help"
            test-id={`ps-node-property-help-${columnId}`}
          >
            {help}
          </div>
        )}
      </div>
    );
  }
);
